import { Link } from "react-router-dom";
import { entityHref } from "~/pages/manifold-nextgen/components/search-manager/constants";
import { SearchHit } from "~/services/search";
import { HitPreview } from "./HitPreview";
import { HitTile } from "./HitTile";
import { HitDetails } from "./useHitDetails";

/** Marks a result link, so arrow keys can move between results. */
export const RESULT_ATTRIBUTE = "data-search-result";

interface SearchResultRowProps {
  hit: SearchHit;
  query: string;
  showRanking: boolean;
  /** From the hit's own listing; undefined until it loads, or for a type with none. */
  details: HitDetails | undefined;
}

/** The title over one line of the text it matched on. */
export const SearchResultRow = ({ hit, query, showRanking, details }: SearchResultRowProps) => (
  <li>
    <Link
      to={entityHref(hit.entity_type, hit.entity_id)}
      {...{ [RESULT_ATTRIBUTE]: "" }}
      className="flex items-center gap-3 rounded-lg px-3 py-2.5 outline-none transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <HitTile hit={hit} details={details} size="md" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-foreground">
          {hit.title || hit.entity_id}
        </span>
        <span className="truncate text-xs leading-5 text-muted-foreground empty:hidden">
          <HitPreview hit={hit} details={details} query={query} />
        </span>
        {showRanking && (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            score {hit.score.toFixed(4)} · vector {formatRank(hit.vector_rank)} · lexical{" "}
            {formatRank(hit.lexical_rank)}
          </span>
        )}
      </span>
    </Link>
  </li>
);

/** A dash when that ranking never returned the entity. */
function formatRank(rank: number | null): string {
  return rank === null ? "—" : `#${rank}`;
}
